import { useMutation } from "@tanstack/react-query";
import { addWorkoutItemAlternate } from "api-client";
import { useTranslation } from "react-i18next";
import { useToast } from "ui";
import { assert } from "utils";

import { useWorkoutsRepository } from "src/contexts/WorkoutsRepositoryContext";
import { useAuthenticatedNavigationContext } from "src/layout/AuthenticatedNavigationContext";

import { useWorkoutItemMutationScope } from "./useWorkoutItemMutationScope";
import { useWorkoutEditStore } from "./WorkoutEditContext";

interface ExerciseAlternateActionsInput {
  workoutId: string;
  workoutItemId: string;
}

export type ExerciseAlternateActions = ReturnType<
  typeof useExerciseAlternateActions
>;

export function useExerciseAlternateActions(
  input: ExerciseAlternateActionsInput,
) {
  const workoutEditStore = useWorkoutEditStore();
  const workoutsRepository = useWorkoutsRepository();
  const toast = useToast();
  const { t } = useTranslation();
  const { withSaving } = useAuthenticatedNavigationContext();
  const scope = useWorkoutItemMutationScope(input.workoutItemId);
  const path = {
    workoutId: input.workoutId,
    workoutItemId: input.workoutItemId,
  };

  const addMutation = useMutation({
    scope,
    mutationFn: async (exerciseId: string) => {
      const response = await withSaving(
        addWorkoutItemAlternate({
          path,
          body: { exerciseId },
        }),
      );
      return response.data.data;
    },
    onSuccess: (data) => {
      const alternate = data.alternateWorkoutItem;
      workoutEditStore.addWorkoutItemAlternate(input.workoutItemId, alternate);
      workoutEditStore.selectAlternate(input.workoutItemId, alternate.id);
    },
    onError: () => {
      toast.toast({
        title: t("workoutItem.addAlternateError"),
        variant: "destructive",
      });
    },
  });

  const removeMutation = useMutation({
    scope,
    mutationFn: (alternateId: string) => {
      return withSaving(
        workoutsRepository.removeWorkoutItemAlternate({
          ...path,
          alternateId,
        }),
      );
    },
    onSuccess: (_, alternateId) => {
      workoutEditStore.removeWorkoutItemAlternate(
        input.workoutItemId,
        alternateId,
      );
    },
    onError: () => {
      toast.toast({
        title: t("workoutItem.removeAlternateError"),
        variant: "destructive",
      });
    },
  });

  const makeMainMutation = useMutation({
    scope,
    mutationFn: (alternateId: string) => {
      return withSaving(
        workoutsRepository.makeWorkoutItemAlternateMain({
          ...path,
          alternateId,
        }),
      );
    },
    // The alternate is the main now, holding the old main as an alternate.
    onSuccess: (data) => {
      assert(data.workoutItem.type === "NORMAL", "Main item is not exercise");
      workoutEditStore.replaceWorkoutItem(
        input.workoutItemId,
        data.workoutItem,
      );
    },
    onError: () => {
      toast.toast({
        title: t("workoutItem.markAsMainError"),
        variant: "destructive",
      });
    },
  });

  const reorderMutation = useMutation({
    scope,
    mutationFn: (alternateIds: string[]) => {
      return withSaving(
        workoutsRepository.reorderWorkoutItemAlternates({
          ...path,
          alternateIds,
        }),
      );
    },
    onMutate: (alternateIds) => {
      const previousIds = getAlternateIds();
      workoutEditStore.reorderWorkoutItemAlternates(
        input.workoutItemId,
        alternateIds,
      );
      return { previousIds };
    },
    onError: (_, __, context) => {
      assert(context, "Reorder has no previous order");
      workoutEditStore.reorderWorkoutItemAlternates(
        input.workoutItemId,
        context.previousIds,
      );
    },
  });

  function getAlternateIds(): string[] {
    return workoutEditStore
      .getAlternatesForWorkoutItem(input.workoutItemId)
      .map((alternate) => {
        return alternate.id;
      });
  }

  return {
    add: addMutation.mutate,
    isAdding: addMutation.isPending,
    remove: removeMutation.mutate,
    makeMain: makeMainMutation.mutate,
    reorder: reorderMutation.mutate,
    getAlternateIds,
    isMakingMain: makeMainMutation.isPending,
    // Queued actions capture ids and order, which a pending one can change.
    isPending:
      addMutation.isPending ||
      removeMutation.isPending ||
      makeMainMutation.isPending ||
      reorderMutation.isPending,
  };
}
